/**
 * @fileoverview Banking data query hook.
 * @description Wraps GET /banking/data in a TanStack query — fetched once and cached, since every
 * page reads from the same full dataset and derives its own numbers client-side via
 * `lib/derived.ts`'s selectors, rather than each page hitting its own endpoint.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import { useQuery } from '@tanstack/react-query'

import { getBankingData } from '@/features/banking/services/banking.service'

export const BANKING_DATA_QUERY_KEY = ['banking', 'data']

/**
 * @description Fetches the full banking dataset.
 * @param {boolean} [enabled] - Set false to skip the fetch (e.g. DashboardShell only needs the
 * bank identity for the header/sidebar when the signed-in user is actually a Banking role).
 * Defaults to true, matching every existing banking-page call site.
 * @returns {ReturnType<typeof useQuery>} The query object (data, isPending, error, ...).
 */
export function useBankingData(enabled = true) {
  return useQuery({
    queryKey: BANKING_DATA_QUERY_KEY,
    queryFn: getBankingData,
    enabled,
  })
}
